/**
 * Constructor Sort Drawer Component
 *
 * Renders sort options as a radio list inside the filters drawer.
 * Listens for cnstrc:sorting-update and updates URL on change.
 */

import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { escapeHtml } from '@theme/constructor-utils';

/**
 * @typedef {object} SortOption
 * @property {string} [value] - Sort value (e.g. 'price-asc')
 * @property {string} [field] - Sort field
 * @property {string} [direction] - Sort direction
 * @property {string} label - Display label
 */

class ConstructorSortDrawerComponent extends Component {
  #controller = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;

    document.addEventListener('cnstrc:sorting-update', this.#handleSortingUpdate, { signal });
    this.addEventListener('change', this.#handleChange, { signal });

    // Initial state from URL
    this.#renderFromUrl();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  /**
   * Get current sort_by from URL
   * @returns {string|null}
   */
  get #currentSortFromUrl() {
    return new URLSearchParams(window.location.search).get('sort_by');
  }

  /**
   * Build default sort options when API hasn't returned yet
   * @returns {{ value: string, label: string }[]}
   */
  #getDefaultOptions() {
    return [
      { value: 'relevance', label: 'Relevance' },
      { value: 'best-selling', label: 'Best selling' },
      { value: 'title-asc', label: 'Alphabetically, A-Z' },
      { value: 'title-desc', label: 'Alphabetically, Z-A' },
      { value: 'price-asc', label: 'Price, low to high' },
      { value: 'price-desc', label: 'Price, high to low' },
      { value: 'created-desc', label: 'Date, new to old' },
      { value: 'created-asc', label: 'Date, old to new' },
    ];
  }

  /**
   * Normalize option to { value, label }
   * @param {SortOption} opt
   * @returns {{ value: string, label: string }}
   */
  #normalizeOption(opt) {
    const value = opt.value ?? (opt.field && opt.direction ? `${opt.field}-${opt.direction}` : '');
    const label = opt.label ?? value;
    return { value: String(value), label: String(label) };
  }

  /**
   * @param {CustomEvent} event
   */
  #handleSortingUpdate = (event) => {
    const { options = [], current, totalResults } = event.detail;
    /** @type {{ value: string, label: string }[]} */
    const normalized = options.length
      ? options.map((/** @type {SortOption} */ o) => this.#normalizeOption(o))
      : this.#getDefaultOptions();
    this.#render(normalized, current || this.#currentSortFromUrl);
    this.#updateSeeResultsCount(totalResults);
  };

  /**
   * Render sort options from URL when no event yet
   */
  #renderFromUrl() {
    const sortBy = this.#currentSortFromUrl;
    this.#render(this.#getDefaultOptions(), sortBy);
  }

  /**
   * Update "See results" button count in drawer when Constructor returns totalResults
   * @param {number} [count]
   */
  #updateSeeResultsCount(count) {
    if (count == null) return;
    const seeResults = document.querySelector('#filters-drawer .facets__see-results');
    if (!seeResults) return;
    const actions =
      /** @type {{ see_items_one?: string; see_items_other?: string } | undefined} */ (
        Theme?.translations?.actions
      );
    const one = actions?.see_items_one ?? 'View result ({{ count }} item)';
    const other = actions?.see_items_other ?? 'View results ({{ count }} items)';
    const template = count === 1 ? one : other;
    seeResults.textContent = template.replace(/\{\{\s*count\s*\}\}/g, String(count));
  }

  /**
   * @param {Array<{ value: string, label: string }>} options
   * @param {string|null} currentValue
   */
  #render(options, currentValue) {
    const refs = this.refs;
    const container = refs?.container;
    const containerEl = Array.isArray(container) ? container[0] : container;
    if (!containerEl || !('innerHTML' in containerEl)) return;

    const sortBy = currentValue || options[0]?.value || '';

    containerEl.innerHTML = `
      <div class="cnstrc-sort-drawer" role="radiogroup" aria-label="${escapeHtml(this.#getSortLabel())}">
        <div class="cnstrc-sort-drawer__heading">${escapeHtml(this.#getSortLabel())}</div>
        <ul class="cnstrc-sort-drawer__list">
          ${options
            .map(
              (opt) => `
            <li class="cnstrc-sort-drawer__item">
              <label class="sorting-filter__option cnstrc-sort-drawer__option">
                <input
                  type="radio"
                  class="cnstrc-sort-drawer__input"
                  name="sort_by"
                  value="${escapeHtml(opt.value)}"
                  ${opt.value === sortBy ? 'checked' : ''}
                />
                <span class="sorting-filter__label cnstrc-sort-drawer__option-label">${escapeHtml(opt.label)}</span>
              </label>
            </li>
          `,
            )
            .join('')}
        </ul>
      </div>
    `;
  }

  #getSortLabel() {
    const actions = /** @type {{ sort?: string } | undefined} */ (Theme?.translations?.actions);
    if (actions?.sort) return actions.sort;
    return 'Sort by';
  }

  /**
   * @param {Event} event
   */
  #handleChange = (event) => {
    const input = event.target;
    if (
      !(input instanceof HTMLInputElement) ||
      !input.matches('.cnstrc-sort-drawer__input') ||
      input.name !== 'sort_by'
    )
      return;

    const url = new URL(window.location.href);
    if (input.value) {
      url.searchParams.set('sort_by', input.value);
    } else {
      url.searchParams.delete('sort_by');
    }
    url.searchParams.delete('page');
    history.pushState({}, '', url.toString());
    document.dispatchEvent(new CustomEvent(ThemeEvents.FilterUpdate));
  };
}

if (!customElements.get('constructor-sort-drawer')) {
  customElements.define('constructor-sort-drawer', ConstructorSortDrawerComponent);
}

export { ConstructorSortDrawerComponent };
